import Button from '@common/Button';
import React, { useCallback, useEffect, useState } from 'react';
import XMarkIcon from '@heroicons/react/24/solid/XMarkIcon';
import Typography from '@common/Typography';
import cx from 'classnames';
import dynamic from 'next/dynamic';

import ProductItem from '@plugin_productitem';
import PaginationSection from '@plugin_productlist/components/PaginationSection';
import CircularProgress from '@common/CircularProgress';
import Show from '@common_show';
import DrawerFilter from '@common_drawer';
import FilterIcon from '@heroicons/react/24/outline/FunnelIcon';

import { getLocalStorage } from '@core/helpers/localstorage';

import Sorting from '@plugin_productlist/components/Shorting';
import ProductListSkeleton from '@plugin_productlist/components/ProductListSkeleton';
import { BREAKPOINTS, COLORS } from '@theme_vars';
import CmsRenderer from '@core_modules/cms/components/cms-renderer';

import FlashSale from '@common/FlashSale';
import useMediaQuery from '@hook/useMediaQuery';

const Filter = dynamic(() => import('@plugin_productlist/components/Filter'));
const WidgetComponent = dynamic(() => import('@core_modules/cms/components/widget/core'), { ssr: true });

const ViewProductList = (props) => {
    const {
        loading, loadmore, products, categoryPath, price, loadPrice,
        handleLoadMore, isPagination, renderEmptyMessage,
        customFilter, aggregations, defaultSort, query, setFiltervalue,
        config, onChangeCategory, dataTabs, onChangeTabs, page, categoryName,
        hasContent, dataCategory, totalCount, q, isPlp, isFlashSale,
        widgetContentTop, loadingWidget,
        ...other
    } = props;

    const { t, storeConfig } = props;

    const { isMobile } = useMediaQuery();
    const [isGrid, setIsGrid] = useState(true);

    const [openDrawerFilter, setOpenDrawerFilter] = useState(false);
    const handleCloseDrawetFilter = () => setOpenDrawerFilter(false);
    const handleOpenDrawerFilter = () => setOpenDrawerFilter(true);

    // Load More (Infinite Loop)
    const [isExceedingOffset, setIsExceedingOffset] = useState(false);
    const handleScroll = useCallback(() => {
        // To get page offset of last user
        // const lastUserLoaded = document.querySelector(`.grid-item:last-child`);
        if (!isPagination) {
            const lastUserLoaded = document.querySelector('.latest-product-indicator');
            if (lastUserLoaded) {
                const lastUserLoadedOffset = lastUserLoaded.offsetTop + lastUserLoaded.clientHeight;
                const pageOffset = window.pageYOffset + window.innerHeight;

                if (pageOffset > lastUserLoadedOffset) {
                    setIsExceedingOffset(true);
                } else {
                    setIsExceedingOffset(false);
                }
            }
        }
    }, [isPagination]);

    React.useEffect(() => {
        if (isExceedingOffset && !loadmore && !isPagination && products.items.length < products.total_count) {
            handleLoadMore();
        }
    }, [isExceedingOffset]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        const gridView = getLocalStorage('isGrid');
        if (gridView !== null) {
            setIsGrid(typeof gridView === 'boolean' ? gridView : JSON.parse(gridView));
        }
    }, [isGrid]);

    const loadList = isPagination ? (loadmore || loading) : loading;

    const ProductSku = [];
    // eslint-disable-next-line no-unused-expressions
    products?.items?.map((item) => ProductSku.push(item.sku));
    const checkEmpty = products.items.length === 0;
    const plpClassSort = 'text-base desktop:font-normal mobile:!font-semibold desktop:text-left mobile:text-center';

    // products count
    const perPage = config?.pageSize;
    const startIndex = (page - 1) * perPage + 1;
    const endIndex = Math.min(page * perPage, totalCount);

    return (
        <>
            <div className="flex flex-row items-start gap-4 desktop:gap-10 w-full">
                <DrawerFilter
                    open={openDrawerFilter}
                    handleClose={handleCloseDrawetFilter}
                    className="z-dialog"
                >
                    <div className="px-6 py-4 flex flex-col">
                        <div className="mb-5 min-h-[36px] border-neutral-100 border-b-[1px] flex items-center justify-between pb-3">
                            <Typography variant="h3" className="text-base font-semibold basis-full">
                                {t('catalog:filter:title')}
                            </Typography>
                            <Button
                                iconOnly
                                icon={<XMarkIcon />}
                                variant="tertiary"
                                onClick={handleCloseDrawetFilter}
                                iconProps={{ className: '!text-neutral-900' }}
                                className="!p-0"
                            />
                        </div>
                        <Show when={openDrawerFilter}>
                            <Filter
                                filter={customFilter || aggregations}
                                defaultSort={JSON.stringify(defaultSort)}
                                filterValue={query}
                                setFiltervalue={setFiltervalue}
                                isSearch={!!config.search}
                                products={products}
                                renderEmptyMessage={renderEmptyMessage}
                                loading={loading}
                                tabs={dataTabs}
                                t={t}
                                onChangeTabs={onChangeTabs}
                                storeConfig={storeConfig}
                                autoReload={false}
                                onSave={handleCloseDrawetFilter}
                            />
                        </Show>
                    </div>
                </DrawerFilter>
                <Show when={loading}>
                    <div className="hidden desktop:inline-flex flex-col w-full desktop:max-w-[270px]">
                        <div className="w-full bg-neutral-100 h-screen" />
                    </div>
                </Show>
                <Show when={!checkEmpty && !loading}>
                    <div
                        className={cx(
                            'swift-filter-container hidden desktop:inline-flex flex-col w-full desktop:max-w-[270px]',
                            'desktop:basis-1/4 desktop:w-1/4',
                        )}
                    >
                        <div className="swift-filter-content w-fullh-screen">
                            <Filter
                                filter={customFilter || aggregations}
                                defaultSort={JSON.stringify(defaultSort)}
                                filterValue={query}
                                setFiltervalue={setFiltervalue}
                                isSearch={!!config.search}
                                products={products}
                                renderEmptyMessage={renderEmptyMessage}
                                loading={loading}
                                tabs={dataTabs}
                                t={t}
                                onChangeTabs={onChangeTabs}
                                storeConfig={storeConfig}
                                scrollContent={false}
                                autoReload
                            />
                        </div>
                    </div>
                </Show>
                <div className="swift-product-list-container basis-full w-full desktop:max-w-full flex flex-col desktop:basis-3/4 desktop:w-3/4 ">
                    <Show when={isMobile}>
                        <FlashSale
                            ProductSku={ProductSku}
                            isPlp
                            isActive={isFlashSale}
                            storeConfig={storeConfig}
                            classWrapperHeader="justify-around py-[8px] px-[5px] bg-[#FFBD00] !mb-4"
                        />
                    </Show>
                    <Show when={!isMobile}>
                        <FlashSale
                            isPlp
                            isActive={isFlashSale}
                            storeConfig={storeConfig}
                            classWrapper="mx-auto sm:w-[50%]"
                            classWrapperHeader="justify-around py-[5px] px-[5px] bg-[#FFBD00] !mb-4"
                        />
                    </Show>
                    <Show when={!loadingWidget}>
                        <div className="cms-block-banner-promo-plp leading-[0px] desktop:px-[0px] tablet:px-[0px] mt-[-10px] mb-[20px]">
                            <WidgetComponent
                                data={widgetContentTop}
                                storeConfig={storeConfig}
                                classNameSkeleton="min-h-12 tablet:!min-h-16 mb-4"
                            />
                        </div>
                    </Show>
                    <Show when={!checkEmpty && !loading}>
                        {categoryName && (
                            <div className="flex flex-row items-center gap-2 title-category">
                                <Typography
                                    variant="h1"
                                    className={
                                        cx('pt-3 text-lg capitalize title-category-text',
                                            'relative z-10 !tracking-normal !text-neutral-black',
                                            'mobile:text-[28px] tablet:text-[30px] desktop:text-[40px]',
                                            'mobile:pb-3 tablet:pb-5 ',
                                            !hasContent && 'pb-0 desktop:pb-4')
                                    }
                                >
                                    {categoryName}
                                </Typography>
                            </div>
                        )}
                        {!categoryName && (
                            <div className="hidden-mobile">
                                <Typography
                                    variant="span"
                                    className={cx(
                                        'flex flex-row justify-between items-center',
                                        'text-xl tracking-normal font-semibold mb-3 !text-neutral-black',
                                    )}
                                >
                                    {t('catalog:title:searchResult')}
                                    {' '}
                                    {`"${q}"`}
                                </Typography>
                            </div>
                        )}
                        <div className={cx(
                            'flex flex-row flex-wrap items-center align-middle justify-between mb-8',
                        )}
                        >
                            <div className={cx(
                                'item-list-total text-neutral-500 text-md tracking-wide',
                                'desktop:w-[auto] tablet:w-full mobile:w-full',
                                'desktop:pb-0 tablet:pb-[10px] mobile:pb-[20px]',
                            )}
                            >
                                {
                                    totalCount <= perPage ? (
                                        <>
                                            {t('catalog:productItem:showing')}
                                            {' '}
                                            {totalCount}
                                            {' '}
                                            {totalCount > 1 ? t('catalog:productItem:products') : t('catalog:productItem:product')}
                                        </>
                                    ) : (
                                        <>
                                            {t('catalog:productItem:showing')}
                                            {' '}
                                            {startIndex}
                                            {' - '}
                                            {endIndex}
                                            {' '}
                                            {t('catalog:productItem:from')}
                                            {' '}
                                            {totalCount}
                                            {' '}
                                            {totalCount > 1 ? t('catalog:productItem:products') : t('catalog:productItem:product')}
                                        </>
                                    )
                                }
                            </div>
                            <div className={cx(
                                'tablet:w-[auto] mobile:w-[50%] desktop:hidden',
                                'tablet:pr-0 mobile:pr-[0.25rem]',
                            )}
                            >
                                <Button
                                    variant="plain"
                                    icon={<FilterIcon />}
                                    iconPosition="left"
                                    iconProps={{
                                        className: 'w-[20px] h-[20px] text-neutral-400',
                                    }}
                                    className={cx(
                                        '!px-[10px] tablet:!py-[8px] mobile:!py-[6px] border border-neutral-300',
                                        'w-full',
                                        'flex justify-center',
                                    )}
                                    classNameText="text-neutral"
                                    onClick={handleOpenDrawerFilter}
                                >
                                    <Typography className="font-semibold text-md !text-neutral-black">
                                        Filter
                                    </Typography>
                                </Button>
                            </div>
                            <Sorting
                                filter={customFilter || aggregations}
                                defaultSort={JSON.stringify(defaultSort)}
                                filterValue={query}
                                setFiltervalue={setFiltervalue}
                                isSearch={!!config.search}
                                t={t}
                                plpClassSort={plpClassSort}
                            />
                        </div>
                    </Show>
                    <div className={
                        cx(
                            'swift-product-list',
                            isGrid
                                ? 'grid gap-2 tablet:gap-5 grid-cols-2 tablet:grid-cols-3 desktop:grid-cols-3'
                                : 'grid grid-cols-1 gap-[10px] tablet:gap-10',
                        )
                    }
                    >
                        <Show when={loading}>
                            <ProductListSkeleton isGrid={isGrid} />
                        </Show>
                        {
                            !loadList && products.items && products.items.map((item, key) => (
                                <div
                                    className="w-auto h-auto swift-item-product-container"
                                    key={key}
                                >
                                    <ProductItem
                                        categorySelect={categoryPath}
                                        isGrid={isGrid}
                                        catalogList
                                        price={price}
                                        loadPrice={loadPrice}
                                        preloadImage={false}
                                        isPlp={isPlp}
                                        {...other}
                                        {...item}
                                    />
                                </div>
                            ))
                        }
                    </div>
                    <div className="latest-product-indicator" />

                    {checkEmpty
                        ? renderEmptyMessage(products.items.length, loading)
                        : null}
                    <Show when={loadmore}>
                        <div className={cx(
                            'w-full p-5 flex justify-center',
                        )}
                        >
                            <div className="flex flex-row">
                                <CircularProgress size="small" className="mr-2" />
                                <Typography varaint="bd-2" color="text-neutral">
                                    Loading...
                                </Typography>
                            </div>
                        </div>
                    </Show>

                    {isPagination && <PaginationSection {...props} />}
                    <Show when={dataCategory?.banner[0]?.description}>
                        <div className="relative w-full h-full mb-1 category-desc mt-[20px]">
                            <div className="from-pagebuilder">
                                {dataCategory?.banner?.length > 0 && dataCategory?.banner[0] && dataCategory?.banner[0]?.description && (
                                    <CmsRenderer content={dataCategory.banner[0].description} storeConfig={storeConfig} />
                                )}
                            </div>
                        </div>
                    </Show>
                </div>
            </div>
            <style jsx global>
                {`
                    .swift-product-list-container .title-category {
                        position: relative;
                    }
                    .swift-product-list-container .title-category:after {
                        position: absolute;
                        content: '';
                        display: block;
                        left: 0;
                        top: 5px;
                        width: 90px;
                        height: 15px;
                        transform: skew(-35deg);
                        background-color: ${COLORS.yellow[300]};
                        z-index: 0;
                    }
                    @media(max-width: ${BREAKPOINTS.mdmax}px) {
                        .swift-product-list-container .title-category:after {
                            left: -20px;
                            top: 10px;
                            width: 60px;
                            height: 10px;
                        }
                        .swift-product-list-container .cms-block-banner-promo-plp .container-scroll-arrow {
                            display: none;
                        }
                    }
                    .swift-product-list-container .banner-category .from-pagebuilder {
                        line-height: 0;
                        margin-top: -10px;
                    }
                `}
            </style>
        </>
    );
};

export default ViewProductList;
